import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Bell,
  BookMarked,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Hash,
  History,
  Home,
  LayoutGrid,
  LayoutTemplate,
  ListTodo,
  Lock,
  LogOut,
  MapPin,
  Menu,
  Pill,
  Search,
  TestTube,
  Scan,
  UserPlus,
  Contact,
  MessageSquare,
  Settings,
  ShieldAlert,
  ShieldCheck,
  TextQuote,
  Users,
} from "lucide-react";
import "materialize-css/dist/css/materialize.min.css";
import "@/styles/emr-dashboard.css";
import "@/styles/emr-appointments-calendar.css";
import "@/styles/emr-patient-chart.css";
import "@/styles/emr-encounters.css";
import "@/styles/emr-appointment-form.css";
import "@/styles/emr-encounter-detail.css";
import "@/styles/emr-catalog.css";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DASH, LOC, PAT, ENC, APT, USR, DX, CPT, MED, LAB, RAD, PHYSREF, REFPROV, SP, PV, SET, PX, AM, SCH, SMS, AC, GRD, PD } from "@/constants/testIds";
import { useAppearance } from "@/context/AppearanceContext";
import { useAuth } from "@/context/AuthContext";
import { canAccessMainTab, filterNavByMainTabAccess, firstAllowedMainPath } from "@/lib/maintabAccess";

const ROLE_LABELS = {
  administrator: "Administrator",
  front_desk: "Front Desk",
  medical_assistant: "Medical Assistant",
  nurse: "Nurse",
  np: "NP",
  pa: "PA",
  md_do: "MD/DO",
  biller: "Biller",
};

const PRIMARY_NAV = [
  { to: "/", label: "Dashboard", icon: Home, testId: DASH.navDashboard, end: true },
  { to: "/organizations", label: "Organizations", icon: Building2, testId: LOC.navOrganizations },
  { to: "/locations", label: "Locations", icon: MapPin, testId: LOC.navLocations },
  { to: "/patients", label: "Patients", icon: Users, testId: PAT.navPatients },
  { to: "/appointments", label: "Appointments", icon: CalendarDays, testId: APT.navAppointments },
  { to: "/encounters", label: "Encounters", icon: ClipboardList, testId: ENC.navEncounters },
  { to: "/icd10-cm", label: "ICD-10-CM", icon: Search, testId: DX.navIcd10 },
  { to: "/cpt", label: "CPT", icon: FileText, testId: CPT.navCpt },
  { to: "/medications", label: "Medications", icon: Pill, testId: MED.navMedications },
  { to: "/labs", label: "Labs", icon: TestTube, testId: LAB.navLabs },
  { to: "/radiology", label: "Radiology", icon: Scan, testId: RAD.navRadiology },
  { to: "/physician-referrals", label: "Physician Referrals", icon: UserPlus, testId: PHYSREF.navPhysicianReferrals },
  { to: "/referring-providers", label: "Referring Providers", icon: Contact, testId: REFPROV.navReferringProviders },
  { to: "/smartphrases", label: "SmartPhrases", icon: TextQuote, testId: SP.navSmartPhrases },
  { to: "/placeholder-vocabulary", label: "Placeholder Vocabulary", icon: Hash, testId: PV.nav },
  { to: "/settings/generic-report-designer", label: "Generic Report Designer", icon: LayoutTemplate, testId: GRD.nav },
  { to: "/page-designer", label: "Page Designer", icon: LayoutGrid, testId: PD.nav },
];

const SETTINGS_NAV = { to: "/settings", label: "Settings", icon: Settings, testId: SET.navSettings };

const PLANNED_NAV = [
  { label: "Reports", icon: BarChart3 },
  { label: "Worklists", icon: ListTodo },
];

function initialsOf(name) {
  const parts = (name || "U").split(/[\s,]+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "U";
}

function primaryRoleLabel(roles) {
  const list = roles || [];
  const preferred = ["md_do", "np", "pa", "nurse", "administrator", "medical_assistant", "front_desk", "biller"];
  const match = preferred.find((role) => list.includes(role)) || list[0];
  return match ? (ROLE_LABELS[match] || match) : "User";
}

function plannedNotice(label) {
  toast.info(`${label} is a planned development area.`);
}

function SidebarNav({ onNavigate, isAdmin, compact, collapsed, onToggleCollapsed }) {
  const { user } = useAuth();
  const maintabAccess = user?.maintabAccess;
  const showLabels = !collapsed;
  const liveItems = [
    ...filterNavByMainTabAccess(PRIMARY_NAV, maintabAccess, { isAdmin }),
    ...(isAdmin
      ? [
          { to: "/users", label: "Users", icon: ShieldCheck, testId: USR.navUsers },
          { to: "/admin/access-control", label: "Access Control", icon: Lock, testId: AC.nav },
          { to: "/settings/px-master", label: "Px Master", icon: BookMarked, testId: PX.navPxMaster },
          { to: "/settings/allergen-master", label: "Allergen Master", icon: ShieldAlert, testId: AM.navAllergenMaster },
          { to: "/admin/system-change-history", label: "System Change History", icon: History, testId: SCH.nav },
          { to: "/admin/sms-sandbox-test", label: "SMS Sandbox Test", icon: MessageSquare, testId: SMS.nav },
        ]
      : []),
    ...filterNavByMainTabAccess([SETTINGS_NAV], maintabAccess, { isAdmin }),
  ];

  return (
    <div className="flex h-full flex-col">
      <div className={compact ? "emr-mz-brand" : "flex items-center gap-3 px-5 py-5"}>
        <div className={compact ? "emr-mz-brand__mark" : "flex h-10 w-10 items-center justify-center rounded-xl bg-teal-500/20 ring-1 ring-teal-400/30"}>
          <Activity className={compact ? "h-4 w-4" : "h-5 w-5 text-teal-300"} />
        </div>
        {showLabels ? (
          <div className="min-w-0 flex-1 leading-tight">
            <div className={compact ? "emr-mz-brand__title" : "text-base font-semibold tracking-tight text-white"}>
              EMRbyMD {compact ? "V3" : <span className="text-teal-300">V3</span>}
            </div>
            <div className={compact ? "emr-mz-brand__sub" : "text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400"}>
              FHIR R4 · US Core
            </div>
          </div>
        ) : null}
        {onToggleCollapsed ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="emr-mz-nav-collapse-btn"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        ) : null}
      </div>

      <nav className={compact ? "emr-mz-nav flex-1 overflow-y-auto" : "flex-1 space-y-1 overflow-y-auto px-3 pb-4"}>
        {liveItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              onClick={onNavigate}
              title={showLabels ? undefined : item.label}
              className={({ isActive }) =>
                compact
                  ? `emr-mz-nav-item ${isActive ? "emr-mz-nav-item--active" : ""}`
                  : `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-teal-600 text-white shadow-sm"
                        : "text-slate-300 hover:bg-white/5 hover:text-white"
                    }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {showLabels ? item.label : null}
            </NavLink>
          );
        })}

        <div className="pt-3">
          {PLANNED_NAV.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                type="button"
                onClick={() => plannedNotice(item.label)}
                title={showLabels ? undefined : item.label}
                className={
                  compact
                    ? "emr-mz-nav-item w-full text-left"
                    : "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-200"
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {showLabels ? item.label : null}
              </button>
            );
          })}
        </div>
      </nav>

      {showLabels ? (
        <div className={compact ? "emr-mz-status mt-auto" : "mt-auto border-t border-white/10 px-5 py-4"}>
          <div className={compact ? "emr-mz-status__label" : "text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"}>
            System status
          </div>
          <div className={compact ? "emr-mz-status__row" : "mt-2 flex items-center gap-2 text-sm text-slate-200"}>
            <span className={compact ? "emr-mz-status__dot" : "h-2 w-2 rounded-full bg-emerald-400"} />
            All systems operational
          </div>
          <div className={compact ? "emr-mz-status__meta" : "mt-3 text-[11px] text-slate-500"}>
            Version 3.1.1
            <br />
            © {new Date().getFullYear()} EMRbyMD
          </div>
        </div>
      ) : null}
    </div>
  );
}

const NAV_COLLAPSE_STORAGE_KEY = "emr-nav-collapsed";

function readNavCollapsed() {
  try {
    return window.localStorage.getItem(NAV_COLLAPSE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export default function AppShell({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsed);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleNavCollapsed = () => {
    setNavCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(NAV_COLLAPSE_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* localStorage may be unavailable (private mode, blocked storage) */
      }
      return next;
    });
  };

  const displayName = user?.displayName || user?.email || "User";
  const roleLabel = primaryRoleLabel(user?.roles);
  const initials = useMemo(() => initialsOf(displayName), [displayName]);

  const onSearch = (event) => {
    event.preventDefault();
    const q = query.trim();
    if (!q) return;
    const access = user?.maintabAccess;
    const accessOpts = { isAdmin };
    if (/^[A-Za-z]\d/.test(q) || q.toLowerCase().includes("icd")) {
      navigate(canAccessMainTab(access, "icd10cm", accessOpts) ? "/icd10-cm" : firstAllowedMainPath(access, accessOpts) || "/");
    } else if (/encounter|visit/i.test(q)) {
      navigate(canAccessMainTab(access, "encounters", accessOpts) ? "/encounters" : firstAllowedMainPath(access, accessOpts) || "/");
    } else if (canAccessMainTab(access, "patients", accessOpts)) {
      navigate("/patients");
    } else {
      navigate(firstAllowedMainPath(access, accessOpts) || "/");
    }
  };

  const { canvasFor } = useAppearance();
  const isDashboard = location.pathname === "/";
  const isPatientsList = location.pathname === "/patients";
  const isPatientChart = /^\/patients\/[^/]+/.test(location.pathname);
  const isEncountersList = location.pathname === "/encounters";
  const isAppointmentForm =
    location.pathname === "/appointments/new" ||
    /^\/appointments\/[^/]+\/edit$/.test(location.pathname);
  const isEncounterWorkspace = /^\/encounters\/[^/]+$/.test(location.pathname);
  const isCatalogList =
    location.pathname === "/medications" ||
    location.pathname === "/labs" ||
    location.pathname === "/radiology" ||
    location.pathname === "/physician-referrals" ||
    location.pathname === "/referring-providers" ||
    location.pathname === "/cpt" ||
    location.pathname === "/organizations" ||
    location.pathname === "/locations" ||
    location.pathname === "/icd10-cm" ||
    location.pathname === "/smartphrases" ||
    location.pathname === "/placeholder-vocabulary" ||
    location.pathname === "/users" ||
    location.pathname === "/admin/access-control" ||
    location.pathname === "/settings/allergen-master" ||
    location.pathname === "/settings/px-master" ||
    location.pathname === "/settings/generic-report-designer" ||
    location.pathname === "/page-designer";
  const isMzChrome =
    isDashboard ||
    isPatientsList ||
    isPatientChart ||
    isEncountersList ||
    isAppointmentForm ||
    isEncounterWorkspace ||
    isCatalogList;
  const isAppointmentsCalendar = /^\/appointments\/?$/.test(location.pathname);

  const shellCanvas = isAppointmentsCalendar
    ? "#E5F0FF"
    : isDashboard
      ? "#F4F7F9"
      : canvasFor(isMzChrome ? "#F4F7F9" : "#f4f7f8");
  const shellClass = isAppointmentsCalendar
    ? " emr-apt-cal-shell"
    : isMzChrome
      ? " emr-mz-dashboard"
      : "";
  return (
    <div className={`flex min-h-screen text-slate-900${shellClass}`} style={{ backgroundColor: shellCanvas }}>
      <aside
        className={`hidden shrink-0 bg-[#0b1f33] lg:block ${
          isMzChrome
            ? `emr-mz-sidenav${navCollapsed ? " emr-mz-sidenav--collapsed" : ""}`
            : navCollapsed
              ? "w-16"
              : "w-[248px]"
        }`}
      >
        <div className="sticky top-0 h-screen">
          <SidebarNav
            isAdmin={isAdmin}
            compact={isMzChrome}
            collapsed={navCollapsed}
            onToggleCollapsed={toggleNavCollapsed}
          />
        </div>
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className={`sidebar-nav-sheet w-[248px] border-0 bg-[#0b1f33] p-0 text-white sm:max-w-[248px]${isMzChrome ? " emr-mz-dashboard emr-mz-sidenav" : ""}`}>
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation</SheetTitle>
          </SheetHeader>
          <SidebarNav isAdmin={isAdmin} onNavigate={() => setMobileOpen(false)} compact={isMzChrome} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className={`sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur${isMzChrome ? " emr-mz-header" : ""}`}>
          <div className={`flex items-center gap-3 px-4 py-3 sm:px-6${isMzChrome ? " emr-mz-header__inner" : ""}`}>
            <Button
              variant="ghost"
              size="icon"
              className={`lg:hidden${isMzChrome ? " emr-mz-icon-btn emr-mz-menu-btn" : ""}`}
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <form onSubmit={onSearch} className={`relative min-w-0 flex-1${isMzChrome ? " emr-mz-searchform" : ""}`}>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search patients, encounters, ICD-10-CM..."
                className={isMzChrome ? "emr-mz-global-search pl-9 text-sm shadow-none" : "h-10 rounded-xl border-slate-200 bg-slate-50 pl-9 pr-12 text-sm shadow-none"}
                aria-label="Global search"
              />
              {!isMzChrome && (
                <span className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400 sm:inline">
                  ⌘K
                </span>
              )}
            </form>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className={`relative${isMzChrome ? " emr-mz-icon-btn" : " rounded-full"}`} aria-label="Notifications">
                  <Bell className="h-5 w-5 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-6 text-center text-sm text-slate-500">
                  No notifications yet
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={isMzChrome ? "emr-mz-userchip flex items-center text-left hover:bg-slate-50" : "flex items-center gap-3 rounded-full py-1 pl-1 pr-2 text-left hover:bg-slate-50"}
                >
                  <Avatar className={isMzChrome ? "emr-mz-avatar h-8 w-8" : "h-9 w-9"}>
                    <AvatarFallback className={isMzChrome ? "bg-[#1976D2] text-[11px] font-semibold text-white" : "bg-teal-700 text-xs font-semibold text-white"}>
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className={isMzChrome ? "emr-mz-userchip__text leading-tight" : "hidden leading-tight md:block"}>
                    <span className={isMzChrome ? "emr-mz-userchip__name block" : "block text-sm font-semibold text-slate-900"}>{displayName}</span>
                    <span className={isMzChrome ? "emr-mz-userchip__role block" : "block text-xs text-slate-500"}>{roleLabel}</span>
                  </span>
                  {isMzChrome && <ChevronDown className="emr-mz-userchip__chevron hidden h-4 w-4 text-slate-400 md:block" aria-hidden="true" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{displayName}</div>
                  {user?.email && <div className="text-xs font-normal text-slate-500">{user.email}</div>}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid={USR.logoutBtn}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main
          className={
            isAppointmentsCalendar
              ? "emr-apt-cal-main flex-1 px-4 py-6 sm:px-6 lg:px-8"
              : isMzChrome
                ? "emr-mz-main flex-1"
                : "flex-1 px-4 py-6 sm:px-6 lg:px-8"
          }
          style={isAppointmentsCalendar ? { backgroundColor: "#E5F0FF" } : undefined}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
